import { cloneElement, isValidElement, useId, type ReactNode } from "react";

import { FieldError } from "./FieldError";

// FormField — DS §4.9. Stacks label (--type-label) → control → helper
// (--type-body-sm) or error (--type-body-sm-strong, same step in 600).
// Auto-associates the label with the single control child (injects id +
// aria-describedby) so getByLabelText works without manual id wiring.

export interface FormFieldProps {
    label?: string;
    /** Visually hide the label (sr-only) while keeping the association —
     *  for designs that show the bare control. */
    labelHidden?: boolean;
    htmlFor?: string;
    /** ReactNode, not string: the helper slot is where an ACTIONABLE hint lives
     *  (the email typo suggestion offers a button that applies itself), and it
     *  is the slot already wired to `aria-describedby`, so the hint is announced
     *  with the field rather than floating unlabelled beside it. Every existing
     *  string caller is unaffected. */
    helper?: ReactNode;
    error?: string;
    optional?: boolean;
    children: ReactNode;
}

export function FormField({
    label,
    labelHidden = false,
    htmlFor,
    helper,
    error,
    optional,
    children,
}: FormFieldProps) {
    const reactId = useId();
    // Resolve the label target to whatever id actually lands on the control:
    // explicit htmlFor → the child's own id → a generated id. This keeps the
    // <label htmlFor> in lockstep with the cloned child's id below, so a
    // pre-id'd control stays label-associated (and getByLabelText-able).
    const childId = isValidElement(children)
        ? (children.props as { id?: string }).id
        : undefined;
    const controlId = htmlFor ?? childId ?? reactId;
    const errorId = `${controlId}-error`;
    const helperId = `${controlId}-helper`;

    let control = children;
    if (label && isValidElement(children)) {
        const child = children as React.ReactElement<{ id?: string; "aria-describedby"?: string }>;
        control = cloneElement(child, {
            id: child.props.id ?? controlId,
            "aria-describedby": child.props["aria-describedby"] ?? (error ? errorId : helper ? helperId : undefined),
        });
    }

    return (
        <div className="flex flex-col">
            {label && (
                <label
                    htmlFor={controlId}
                    className={
                        labelHidden
                            ? "sr-only"
                            // --type-label (600 14px/1.4) rather than
                            // hand-assembled classes: the token existed and
                            // said 14px while every label in the product
                            // rendered 12px, so the DS had a stated label style
                            // nothing used. text-secondary because a label
                            // names the field, it does not compete with the
                            // value the therapist typed into it.
                            //
                            // `capitalize` — Title Case, every word: "Legal
                            // First Name". Ruled 2026-08-31, overriding the
                            // house sentence-case rule for FIELD LABELS
                            // specifically. Applied in CSS rather than by
                            // rewriting 20 label strings, so the casing is one
                            // decision in one place and the source copy stays
                            // readable in sentence case.
                            // flex + justify-between, not `block`: the optional
                            // marker is pushed to the FAR RIGHT of the row
                            // rather than trailing the label text. It answers
                            // "do I have to fill this in?", which is a question
                            // asked of the FIELD, and at the right edge it
                            // lines up into a column the eye can scan down
                            // instead of landing in a different place on every
                            // row depending on how long the label is.
                            //
                            // items-baseline so the 12px marker sits on the
                            // 14px label's baseline; gap-4 keeps a long label
                            // from touching it before it wraps.
                            : "mb-2 flex items-baseline justify-between gap-4 [font:var(--type-label)] capitalize text-secondary"
                    }
                >
                    {/* Wrapped so the label text is ONE flex child. Bare, a
                        multi-word label would be split across children and
                        justify-between would space the words out. */}
                    <span>{label}</span>
                    {/* A real space node between the label and the marker.
                        Whitespace-only text between flex items is not laid out,
                        so this costs nothing visually — but it IS part of the
                        accessible name, and without it assistive tech reads
                        "Phoneoptional" as one word (Copilot a11y finding, PR
                        #1092). It used to be carried by the "· " that preceded
                        the marker; the dot is gone and the space is not. */}
                    {optional && " "}
                    {optional && (
                        // Upright, not italic. The italic was doing the work of
                        // setting this apart from the label; sitting at the far
                        // edge in --text-tertiary it is already apart, and
                        // italic at 12px in a UI face is the least legible
                        // thing on the row.
                        //
                        // `shrink-0` so it holds its width and the LABEL wraps
                        // first — the marker breaking over two lines is worse
                        // than a wrapped label.
                        //
                        // No leading &middot;. The dot was a SEPARATOR from the
                        // label text it used to sit against; at the far edge it
                        // separates nothing and reads as a stray bullet.
                        <span className="shrink-0 font-sans text-xs font-normal text-tertiary">
                            optional
                        </span>
                    )}
                </label>
            )}
            {control}
            {error ? (
                <FieldError id={errorId}>{error}</FieldError>
            ) : helper ? (
                <div
                    id={helperId}
                    className="mt-2 [font:var(--type-body-sm)] text-tertiary"
                >
                    {helper}
                </div>
            ) : null}
        </div>
    );
}
